<template>
  <div class="bg-background-fg/3 p-4">
    <div class="mb-2 flex items-center justify-between">
      <button type="button" @click="decrementCurrentMonth">
        <IconChevronLeft />
      </button>
      <p class="font-semibold">
        {{ DateFns.format(currentMonthStart, "MMMM yyyy") }}
      </p>
      <button type="button" @click="incrementCurrentMonth">
        <IconChevronRight />
      </button>
    </div>
    <div class="mb-2 grid grid-cols-7 grid-rows-7 gap-1">
      <template
        v-for="dayOfWeek in daysOfWeek"
        :key="dayOfWeek"
      >
        <div
          class="flex items-center justify-center text-sm text-background-fg/70"
        >
          {{ dayOfWeek }}
        </div>
      </template>
      <template
        v-for="date in visibleDates"
        :key="date.toString()"
      >
        <div :class="$style.squareHack">
          <button
            type="button"
            :class="[
              isDateActive(date)
                ? 'bg-primary text-primary-fg'
                : 'bg-background text-background-fg',
              DateFns.isSameMonth(date, currentMonthStart)
                ? ''
                : 'opacity-20',
              'absolute inset-0 w-full rounded font-semibold disabled:opacity-5',
            ]"
            :disabled="isDateDisabled(date)"
            @click="handleClickDate(date)"
          >
            {{ DateFns.getDate(date) }}
          </button>
        </div>
      </template>
    </div>

    <div class="flex items-center justify-between">
      <button
        type="button"
        class="rounded bg-background px-2 py-1"
        @click="setModelToToday"
      >
        Today
      </button>
      <template v-if="allowAllTime">
        <button
          type="button"
          class="rounded bg-background px-2 py-1"
          @click="setModelToAllTime"
        >
          All Time
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import * as DateFns from "date-fns"
const props = withDefaults(
  defineProps<{
    modelValue: string | null
    disableFuture?: boolean
    allowAllTime?: boolean
  }>(),
  { disableFuture: false, allowAllTime: false }
)
const emit = defineEmits<{
  (event: "update:modelValue", value: string | null): void
}>()

const getCurrentMonthStart = (date: Date | null) => {
  return date
    ? DateFns.startOfMonth(date)
    : DateFns.startOfMonth(new Date())
}

const internalModel = computed({
  get: () =>
    props.modelValue
      ? DateFns.parse(
          props.modelValue,
          "yyyy-MM-dd",
          new Date()
        )
      : null,
  set: (value: Date | null) =>
    value
      ? emit(
          "update:modelValue",
          DateFns.format(value, "yyyy-MM-dd")
        )
      : emit("update:modelValue", null),
})
const isDrawerVisible = ref(false)

const hideDrawer = () => {
  isDrawerVisible.value = false
}

const currentMonthStart = ref(
  getCurrentMonthStart(internalModel.value)
)
const decrementCurrentMonth = () => {
  currentMonthStart.value = DateFns.addMonths(
    currentMonthStart.value,
    -1
  )
}

const incrementCurrentMonth = () => {
  const nextMonthStart = DateFns.addMonths(
    currentMonthStart.value,
    1
  )
  if (
    props.disableFuture &&
    DateFns.isFuture(nextMonthStart)
  )
    return
  currentMonthStart.value = nextMonthStart
}

const visibleDates = computed(() =>
  DateFns.eachDayOfInterval({
    start: DateFns.startOfISOWeek(currentMonthStart.value),
    end: DateFns.endOfISOWeek(
      DateFns.endOfMonth(currentMonthStart.value)
    ),
  })
)

const handleClickDate = (date: Date) => {
  internalModel.value = date
  hideDrawer()
}

const isDateActive = (date: Date) => {
  if (!internalModel.value) return false
  return DateFns.isSameDay(date, internalModel.value)
}

const isDateDisabled = (date: Date) => {
  if (props.disableFuture && DateFns.isFuture(date))
    return true
  return false
}

const daysOfWeek = [
  "Mo",
  "Tu",
  "We",
  "Th",
  "Fr",
  "Sa",
  "Su",
]

const setModelToToday = () => {
  internalModel.value = new Date()
  hideDrawer()
}

const setModelToAllTime = () => {
  internalModel.value = null
  hideDrawer()
}
</script>

<style lang="scss" module>
.squareHack {
  position: relative;
  padding-top: 100%;
}
</style>
